import NavBar from "../../components/NavBar/NavBar";
import Footer from "../../components/Footer/Footer";
import Button from "../../components/Button/Button";
import Checkmark from "../../components/Checkmark/Checkmark";
import "./ProductPage.scss";
import interviewCoaching from "../../assets/products/interview-coaching.jpg";
import { 
  FaUsers, 
  FaUserTie, 
  FaComments, 
  FaLightbulb, 
  FaClipboardCheck, 
  FaClock,
  FaVideo,
  FaCalendarAlt
} from "react-icons/fa";

interface InterviewCoachingPageProps {
  product: {
    id: string;
    price: number;
    length: string;
    comingSoon: boolean;
    checkoutUrl?: string;
  };
}

const InterviewCoachingPage = ({ product }: InterviewCoachingPageProps) => {
  const coachingFeatures = [
    {
      icon: <FaUserTie />,
      title: "Experienced Coach",
      description: "Individualised feedback from an experienced Ophthalmologist who has completed the interview process to a high standard"
    },
    {
      icon: <FaUsers />,
      title: "Panel Insights",
      description: "Insights from consultant ophthalmologists who serve on real interview panels"
    },
    {
      icon: <FaComments />,
      title: "Scenario Practice",
      description: "Practice delivery of high-yield scenarios (clinical, communication, professionalism)"
    },
    {
      icon: <FaLightbulb />,
      title: "Answer Techniques",
      description: "Techniques for answering challenging questions with clarity and confidence"
    },
    {
      icon: <FaClipboardCheck />,
      title: "Mock Interviews",
      description: "Mock interview runs with targeted coaching on structure and scoring strategy"
    },
    {
      icon: <FaClock />,
      title: "Full Day Preparation",
      description: "2 sessions (10 hours total) across a full day for comprehensive preparation"
    }
  ];

  const formatFeatures = [
    {
      icon: <FaClock />,
      title: "Full Day Sessions",
      description: "2 comprehensive coaching sessions across a full day"
    },
    {
      icon: <FaVideo />,
      title: "Online Delivery",
      description: "Online delivery via video conferencing (most likely format)"
    },
    {
      icon: <FaComments />,
      title: "Real-Time Feedback",
      description: "Real-time feedback and interactive practice"
    },
    {
      icon: <FaCalendarAlt />,
      title: "Flexible Scheduling",
      description: "Flexible scheduling to fit your preparation timeline"
    }
  ];
  return (
    <>
      <NavBar />
      <div id="ProductPage">
        {/* Hero Section */}
        <div className="product-hero-section">
          <div className="product-hero-background">
            <div className="product-hero-left"></div>
            <div 
              className="product-hero-image-bg"
              style={{
                backgroundImage: `url(${interviewCoaching})`
              }}
            ></div>
          </div>

          <div className="product-hero-overlay">
            <div className="site-container">
              <div className="product-hero-content">
                <div className="interview-coaching-limited-badge">
                  <FaUsers aria-hidden />
                  <span>Limited spaces — book early to secure your place</span>
                </div>
                <h1>Interview Coaching</h1>
                <p className="product-description">
                  Achieve peak performance in your Ophthalmology interview with structured, one-to-one coaching sessions. Get personalised guidance from an experienced Ophthalmologist who has completed the interview process to a high standard.
                </p>
                <div className="product-pricing">
                  <span className="price">£{product.price}</span>
                  <span className="length">/ {product.length}</span>
                  {product.comingSoon && (
                    <span className="coming-soon-pill">coming soon</span>
                  )}
                </div>
                {!product.comingSoon && (
                  <Button
                    text="Buy now"
                    linkTo={product.checkoutUrl ?? `/sign-up?product=${product.id}`}
                    type="navy"
                    external={!!product.checkoutUrl}
                  />
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Mobile/Tablet Image - Hidden on Desktop */}
        <div className="interview-coaching-mobile-image" style={{
          display: "none",
          width: "100%",
          maxWidth: "450px",
          margin: "0 auto",
          padding: "0 20px"
        }}>
          <img 
            src={interviewCoaching} 
            alt="Interview Coaching"
            style={{
              width: "100%",
              height: "auto",
              display: "block",
              borderRadius: "8px",
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)"
            }}
          />
        </div>

        {/* Main Content */}
        <div className="product-sales-content">
          <div className="site-container">
            
            {/* Introduction Section */}
            <section className="sales-section intro-section">
              <p className="lead-text">
                The Ophthalmology ST interview is highly competitive and requires more than just clinical knowledge. Success depends on your ability to communicate clearly, demonstrate professionalism, and present yourself confidently under pressure.
              </p>
              <p className="solution-text">
                <strong>Get personalised coaching from someone who's been there and succeeded.</strong>
              </p>
            </section>

            {/* Coaching Features Section */}
            <section className="sales-section">
              <h2 style={{ fontSize: "2rem", fontWeight: "bold", color: "#0e4f99", marginBottom: "20px", textAlign: "center" }}>
                What's Included
              </h2>
              <p style={{ fontSize: "1.1rem", textAlign: "center", color: "#666", marginBottom: "40px", maxWidth: "800px", margin: "0 auto 40px" }}>
                Achieve peak performance with structured, one-to-one coaching sessions tailored to you — focusing on refining responses, sharpening clinical reasoning, and improving delivery under real-world interview conditions.
              </p>
              
              <div className="feature-grid" style={{ gridTemplateColumns: "repeat(3, 1fr)", gap: "25px", marginTop: "30px" }}>
                {coachingFeatures.map((feature, index) => (
                  <div key={index} className="feature-card" style={{ 
                    background: "white", 
                    padding: "25px", 
                    borderRadius: "12px", 
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
                    transition: "all 0.2s ease",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start"
                  }} onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-3px)";
                    e.currentTarget.style.boxShadow = "0 6px 16px rgba(0, 0, 0, 0.12)";
                  }} onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "0 2px 8px rgba(0, 0, 0, 0.08)";
                  }}>
                    <div style={{
                      color: "#0e4f99",
                      fontSize: "2rem",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "60px",
                      height: "60px",
                      background: "rgba(14, 79, 153, 0.1)",
                      borderRadius: "12px",
                      marginBottom: "15px"
                    }}>
                      {feature.icon}
                    </div>
                    <h3 style={{ 
                      fontSize: "1.2rem", 
                      fontWeight: "bold", 
                      color: "#0e4f99", 
                      marginBottom: "10px",
                      marginTop: 0
                    }}>
                      {feature.title}
                    </h3>
                    <p style={{ 
                      fontSize: "0.95rem", 
                      color: "#666", 
                      lineHeight: "1.6",
                      margin: 0
                    }}>
                      {feature.description}
                    </p>
                  </div>
                ))}
              </div>
            </section>

            {/* Format & Delivery Section */}
            <section className="sales-section" style={{ marginTop: "60px" }}>
              <div style={{
                background: "linear-gradient(135deg, #f0f7ff 0%, #e8f4fd 100%)",
                borderRadius: "16px",
                padding: "40px",
                border: "1px solid rgba(14, 79, 153, 0.1)"
              }}>
                <h2 style={{ 
                  fontSize: "1.8rem", 
                  fontWeight: "bold", 
                  color: "#0e4f99", 
                  marginBottom: "12px",
                  textAlign: "center"
                }}>
                  Format & Delivery
                </h2>
                <p style={{ 
                  fontSize: "1rem", 
                  textAlign: "center", 
                  color: "#666", 
                  marginBottom: "35px",
                  maxWidth: "700px",
                  margin: "0 auto 35px"
                }}>
                  Our interview coaching is designed to be accessible and effective. Sessions are conducted online, making it convenient for you to participate from anywhere.
                </p>
                
                <div className="format-delivery-grid" style={{
                  display: "grid",
                  // gridTemplateColumns: "repeat(4, 1fr)",
                  gap: "20px",
                  maxWidth: "1000px",
                  margin: "0 auto"
                }}>
                  {formatFeatures.map((feature, index) => (
                    <div key={index} style={{ 
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      textAlign: "center",
                      padding: "15px"
                    }}>
                      <div style={{
                        color: "#0e4f99",
                        fontSize: "2rem",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "50px",
                        height: "50px",
                        background: "white",
                        borderRadius: "12px",
                        marginBottom: "12px",
                        boxShadow: "0 2px 6px rgba(0, 0, 0, 0.08)"
                      }}>
                        {feature.icon}
                      </div>
                      <h3 style={{ 
                        fontSize: "1rem", 
                        fontWeight: "600", 
                        color: "#0e4f99", 
                        marginBottom: "6px",
                        marginTop: 0
                      }}>
                        {feature.title}
                      </h3>
                      <p style={{ 
                        fontSize: "0.85rem", 
                        color: "#666", 
                        lineHeight: "1.5",
                        margin: 0
                      }}>
                        {feature.description}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Limited Availability Notice */}
            <section className="sales-section intro-section" style={{ background: "#fff3cd", padding: "25px", borderRadius: "8px", border: "2px solid #ffc107" }}>
              <p className="lead-text" style={{ margin: 0, color: "#856404", fontWeight: "600" }}>
                <FaUsers style={{ marginRight: "10px", display: "inline" }} />
                Limited spots available — book early to secure your place
              </p>
            </section>

            {/* CTA Section */}
            <section className="sales-section pricing-section">
              <div className="pricing-card">
                <h3>Get Started Today</h3>
                <div className="pricing-amount" style={{ marginBottom: "15px" }}>
                  <div style={{ display: "flex", alignItems: "baseline", justifyContent: "center", gap: "8px", marginBottom: product.comingSoon ? "10px" : "0" }}>
                    <span className="price-large">£{product.price}</span>
                    <span className="price-period">/ {product.length}</span>
                  </div>
                  {product.comingSoon && (
                    <div style={{ display: "flex", justifyContent: "center" }}>
                      <span className="coming-soon-pill">coming soon</span>
                    </div>
                  )}
                </div>
                <p style={{ marginBottom: "10px", color: "#666" }}>
                  2 sessions (10 hours total) across a full day
                </p>
                <p style={{ marginBottom: "20px", color: "#666", fontSize: "0.95rem" }}>
                  Contact us to discuss your interview coaching needs and get started.
                </p>
                <div className="pricing-cta">
                  {!product.comingSoon && (
                    <Button
                      text="Buy now"
                      linkTo={product.checkoutUrl ?? `/sign-up?product=${product.id}`}
                      type="navy"
                      external={!!product.checkoutUrl}
                    />
                  )}
                </div>
              </div>
            </section>

          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default InterviewCoachingPage;

